"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";
import { useRouter } from "next/navigation";

import toast from "react-hot-toast";
import Button from "@/designs/Button";
import { useAuthLoginMutation } from "@/graphql/generated/schema";
import Input from "@/designs/Input";
import { twMerge } from "tailwind-merge";

const LoginPage = () => {
  const router = useRouter();
  const [login, { loading }] = useAuthLoginMutation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please enter both email and password.");
      return;
    }

    try {
      const { data } = await login({
        variables: { email, password },
      });

      if (data?.authLogin?.error) {
        toast.error(data.authLogin.error.message);
        return;
      }

      if (data?.authLogin?.token) {
        localStorage.setItem("token", data.authLogin.token);
        router.push("/"); // or any protected route
      }
    } catch (err) {
      console.error(err);
      toast.error("Login failed. Please try again.");
    }
  };

  return (
    <div
      className={twMerge(
        "flex flex-col md:flex-row md:h-screen overflow-y-auto bg-gray-50",
      )}
    >
      {/* Left Side: Logo */}
      <div
        className={twMerge(
          "flex justify-center items-center w-full md:w-1/2 md:h-full transition-colors bg-white",
        )}
      >
        <Image
          src="/Logo.jpg"
          width={700}
          height={250}
          alt="logo"
          className="object-contain"
          priority
        />
      </div>

      {/* Right Side: Login Form */}
      <div className="flex justify-center items-center bg-[#004175] w-full md:w-1/2 h-full p-6 overflow-y-auto">
        <div
          className={twMerge(
            "w-full max-w-md rounded-2xl shadow-xl p-8 transition-colors border bg-white border-transparent",
          )}
        >
          <div className="flex flex-col items-center mb-8">
            <h1 className={twMerge(" font-bold mt-4 transition-colors ")}>
              Welcome Back
            </h1>
            <p className={twMerge("transition-colors text-gray-600")}>
              Log in to manage your visitors
            </p>
          </div>
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <Input
                type="email"
                label="Email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(value) => setEmail(value)}
                className="w-full px-4 py-2 text-black border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#004175]"
              />
            </div>

            <div>
              <Input
                type="password"
                placeholder="••••••••"
                label="Password"
                required
                value={password}
                onChange={(value) => setPassword(value)}
                className="w-full px-4 py-2 border border-gray-300 text-black rounded-md focus:outline-none focus:ring-2 focus:ring-[#004175]"
              />
            </div>

            <div className="text-right text-sm">
              <Link
                href="/forgotPassword"
                className="text-[#004175] hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button type="submit" loading={loading}>
              Login
            </Button>

            <div className="text-right text-sm">
              <Link href="/signup" className="text-[#004175] hover:underline">
                Don't have an account?{" "}
                <span className="font-semibold">Sign Up</span>
              </Link>
            </div>

            <div className="flex justify-center gap-4 text-xs pt-4 border-t mt-4 text-gray-400">
              <Link
                href="/privacy-policy"
                className="hover:text-[#004175] hover:underline transition-colors uppercase font-medium"
              >
                Privacy Policy
              </Link>
              <span>•</span>
              <Link
                href="/support"
                className="hover:text-[#004175] hover:underline transition-colors uppercase font-medium"
              >
                Support
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
